import { PaginatedRequest, PaginatedResponse } from '@/types';
import { CASES_PREFIX } from '@/pages/CustomerService/constants';
import { customerServiceApi } from '@/pages/CustomerService/services/customer-service.service';
import {
  CaseApi,
  ViewCaseApi,
  CaseFilter,
  CaseSchema,
  CaseCreatePayload,
  CaseFinalizationSchema,
  CaseDetailsSchema,
} from '../models';

/** Departamento y localidad que aparecen en los casos, con su cantidad. */
export interface CaseDestination {
  department: string;
  city: string | null;
  cases: number;
}

const casesApi = customerServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getCases: builder.query<
    PaginatedResponse<CaseApi>,
    { filters?: Partial<CaseFilter>; paginated: PaginatedRequest }>({
      query: ({ filters, paginated }) => ({
        url: CASES_PREFIX,
        params: {
          shippingId: filters?.shippingId,
          priorityId: filters?.priorityId,
          ...(filters?.issueIds && filters.issueIds.length > 0
            ? { issueIds: filters.issueIds.join(',') }
            : {}),
          status: filters?.caseStatusId,
          createUser: filters?.createUser,
          businessId: filters?.businessId,
          driverId: filters?.driverId,
          assigneeId: filters?.assigneeId || undefined,
          finalizationReasonId: filters?.finalizationReasonId,
          financialResponsibilityId: filters?.financialResponsibilityId,
          ...(filters?.dateField === 'finalizationDt'
            ? {
                finalizationDateFrom: filters?.dateFrom?.toISOString(),
                finalizationDateTo: filters?.dateTo?.toISOString(),
              }
            : {
                dateFrom: filters?.dateFrom?.toISOString(),
                dateTo: filters?.dateTo?.toISOString(),
              }),
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        }
      }),
      providesTags: ['Cases'],
    }),
    getCase: builder.query<ViewCaseApi, { caseId: string }>({
      query: ({ caseId }) => ({
        url: `${CASES_PREFIX}/${caseId}`,
      }),
      providesTags: ['Case'],
    }),
    createCase: builder.mutation<CaseApi, CaseCreatePayload>({
      query: (data) => ({
        url: CASES_PREFIX,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Cases'],
    }),
    updateCase: builder.mutation<CaseApi, { caseId: string; data: CaseSchema }>(
      {
        query: ({ caseId, data }) => ({
          url: `${CASES_PREFIX}/${caseId}`,
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: ['Cases', 'Case'],
      }
    ),
    finishCase: builder.mutation<
      CaseApi,
      { finalizationUser: string; caseId: string; data: CaseFinalizationSchema }
    >({
      query: ({ finalizationUser, caseId, data }) => ({
        url: `${CASES_PREFIX}/${caseId}/change-status-to-finished`,
        method: 'PATCH',
        body: {
          ...data,
          // El form maneja el monto como texto; vacío = sin monto.
          finalizationAmount: data.finalizationAmount
            ? Number(data.finalizationAmount)
            : null,
          finalShippingStatus: data.finalShippingStatus.trim() || null,
          finalizationUser,
        },
      }),
      invalidatesTags: ['Cases', 'Case', 'CasesOverview'],
    }),
    updateCaseDetails: builder.mutation<
      ViewCaseApi,
      { caseId: string; user: string; data: CaseDetailsSchema }
    >({
      query: ({ caseId, user, data }) => ({
        url: `${CASES_PREFIX}/${caseId}/details`,
        method: 'PATCH',
        body: { ...data, user },
      }),
      invalidatesTags: ['Cases', 'Case', 'CaseInterventions', 'CasesOverview'],
    }),
    updateFinalizationAmount: builder.mutation<
      ViewCaseApi,
      { caseId: string; user: string; finalizationAmount: number | null }
    >({
      query: ({ caseId, user, finalizationAmount }) => ({
        url: `${CASES_PREFIX}/${caseId}/finalization-amount`,
        method: 'PATCH',
        body: { finalizationAmount, user },
      }),
      invalidatesTags: [
        'Cases',
        'Case',
        'CaseInterventions',
        'CasesOverview',
        'Minute',
        'Invoices',
      ],
    }),
    getCaseDestinations: builder.query<CaseDestination[], void>({
      query: () => ({ url: `${CASES_PREFIX}/destinations` }),
      providesTags: ['CaseDestinations'],
    }),
    assignCase: builder.mutation<
      CaseApi,
      { caseId: string; assigneeId: string | null; assigneeName: string | null }
    >({
      query: ({ caseId, assigneeId, assigneeName }) => ({
        url: `${CASES_PREFIX}/${caseId}/assignee`,
        method: 'PATCH',
        body: { assigneeId, assigneeName },
      }),
      invalidatesTags: ['Cases', 'Case'],
    }),
    inProcessCase: builder.mutation<
      CaseApi,
      { caseId: string; inProcessUser: string }
    >({
      query: ({ caseId, inProcessUser }) => ({
        url: `${CASES_PREFIX}/${caseId}/change-status-to-in-process`,
        method: 'PATCH',
        body: { inProcessUser: inProcessUser },
      }),
      invalidatesTags: ['Cases', 'Case'],
    }),
    addImage: builder.mutation<any, any>({
      query: ({ caseId, formData }) => ({
        url: `${CASES_PREFIX}/${caseId}/documents`,
        body: formData,
        method: 'POST',
      }),
      invalidatesTags: ['Images'],
    }),
    deleteImage: builder.mutation<any, any>({
      query: ({ caseId, id }) => ({
        url: `${CASES_PREFIX}/${caseId}/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Images'],
    }),
    getImages: builder.query<any, { caseId: string }>({
      query: ({ caseId }) => ({
        url: `${CASES_PREFIX}/${caseId}/documents`,
      }),
      providesTags: ['Images'],
    }),
  }),
});

export const {
  useGetCasesQuery,
  useGetCaseQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useFinishCaseMutation,
  useInProcessCaseMutation,
  useAssignCaseMutation,
  useUpdateCaseDetailsMutation,
  useUpdateFinalizationAmountMutation,
  useGetCaseDestinationsQuery,
  useAddImageMutation,
  useDeleteImageMutation,
  useGetImagesQuery,
} = casesApi;
